/**
 * useBadges — ONE request for every nav/header counter, plus live push.
 *
 * Replaces six independent polling loops (news unread, ticket unread,
 * order ids, transaction ids, service queue, recharge reviews) with a
 * single `GET /api/badges/summary`.
 *
 * Freshness comes from the server, not from a fast timer:
 *   - `badges:changed` over Socket.IO (new order, admin reply, recharge,
 *     status change, published news) triggers an immediate refetch;
 *   - the HTTP poll is only a 5-minute safety net and, like every poll in
 *     this app, is skipped while the tab is hidden (useSmartPoll);
 *   - local actions (opening a ticket, reading news) fire the unread bus
 *     and refresh instantly too.
 *
 * Refetches are throttled to one per 2 s so a burst of socket events
 * can't turn into a request storm.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useSmartPoll } from './useSmartPoll';
import { acquireSocket, releaseSocket } from '../lib/socketClient';
import { subscribeUnreadRefresh } from '../lib/unreadBus';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_GAP_MS = 2000;

export default function useBadges({ token, isAdmin = false } = {}) {
  const [badges, setBadges] = useState(null);
  const [version, setVersion] = useState(0);
  const lastRef = useRef(0);

  const load = useCallback(async () => {
    if (!token) return undefined;
    const r = await fetch(`${API_URL}/api/badges/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return false;
    const j = await r.json();
    lastRef.current = Date.now();
    setBadges(j);
    setVersion((v) => v + 1);
    return true;
  }, [token]);

  // Coalesced variant for event-driven refreshes.
  const refresh = useCallback(() => {
    if (Date.now() - lastRef.current < MIN_GAP_MS) return;
    lastRef.current = Date.now();
    load().catch(() => {});
  }, [load]);

  useSmartPoll(load, 300000, { enabled: !!token });

  // Live push — admins listen on the `admins` room, customers on their
  // own `user_<id>` room. Both receive `badges:changed`.
  useEffect(() => {
    if (!token) return undefined;
    const socket = acquireSocket();

    const join = () => {
      socket.emit(isAdmin ? 'join_admin' : 'join_user', { token }, () => {});
    };
    const onChanged = () => refresh();

    socket.on('connect', join);
    socket.on('badges:changed', onChanged);
    if (socket.connected) join();

    return () => {
      socket.off('connect', join);
      socket.off('badges:changed', onChanged);
      releaseSocket();
    };
  }, [token, isAdmin, refresh]);

  // Local UI actions (ticket opened, news read) → instant recount.
  useEffect(() => subscribeUnreadRefresh(refresh), [refresh]);

  return { badges, version, refresh };
}
